import { useCallback, useEffect, useState } from 'react';

import { useChangeParams } from 'app/modules/query-state/hooks';
import { useQueryParamStore } from 'app/modules/query-state/store';
import { DEFAULT_DEVICE_SENSOR_FILTER } from 'app/modules/system-settings/devices/core/constant';
import { DeviceSensorFilterParams } from 'app/modules/system-settings/devices/core/type';

import isEqual from 'lodash/isEqual';

const useDeviceSensorFilter = (
  openFilter: boolean,
  setIsOpenFilter: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  const onChangeParams = useChangeParams().onChangeParams;
  const curParams = useQueryParamStore((s) => s.state.curParams);

  const [filter, setFilter] = useState<DeviceSensorFilterParams>(DEFAULT_DEVICE_SENSOR_FILTER);
  const [menuIsOpen, setMenuIsOpen] = useState<{
    status: boolean;
    locationIds: boolean;
  }>({
    status: false,
    locationIds: false,
  });

  useEffect(() => {
    if (!openFilter) return;
    setFilter({
      locationIds: Array.isArray(curParams.locationIds) ? curParams.locationIds : [],
      status: Array.isArray(curParams.status) ? curParams.status : [],
    });
  }, [curParams, openFilter]);

  const handleCloseFilter = () => {
    setIsOpenFilter(false);
  };

  const handleApplyFilter = () => {
    onChangeParams({
      ...filter,
      page: 1,
    });
    handleCloseFilter();
  };

  const handleClearAll = () => {
    setFilter(DEFAULT_DEVICE_SENSOR_FILTER);
    onChangeParams(DEFAULT_DEVICE_SENSOR_FILTER);
    handleCloseFilter();
  };

  const handleSelectChange = useCallback(
    (key: keyof DeviceSensorFilterParams) => (newValue: { value: string }[]) => {
      setFilter((prev) => ({
        ...prev,
        [key]: newValue.map((el) => String(el.value)),
      }));
      setMenuIsOpen((prev) => ({
        ...prev,
        [key]: true,
      }));
    },
    [],
  );

  const handleMenuClose = (key: keyof typeof menuIsOpen) => () =>
    setMenuIsOpen({ ...menuIsOpen, [key]: false });

  const handleMenuOpen = (key: keyof typeof menuIsOpen) => () =>
    setMenuIsOpen({ ...menuIsOpen, [key]: true });

  return {
    filter,
    menuIsOpen,
    handleClearAll,
    handleMenuOpen,
    handleMenuClose,
    handleSelectChange,
    handleApplyFilter,
  };
};

export default useDeviceSensorFilter;
